import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { db } from '../config/firebase';
import { doc, onSnapshot, setDoc, updateDoc, increment } from 'firebase/firestore';

interface WalletContextType {
  balance: number;
  updateBalance: (amount: number) => Promise<void>;
  loading: boolean;
}

const WalletContext = createContext<WalletContextType | undefined>(undefined);

export const useWallet = () => {
  const context = useContext(WalletContext);
  if (!context) {
    throw new Error('useWallet must be used within a WalletProvider');
  }
  return context;
};

export const WalletProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [balance, setBalance] = useState(1000); // Starting balance
  const [loading, setLoading] = useState(true);
  const { currentUser } = useAuth();

  useEffect(() => {
    if (!currentUser) {
      setBalance(0);
      setLoading(false);
      return;
    }

    const walletRef = doc(db, 'wallets', currentUser.uid);
    
    // Initialize wallet if it doesn't exist
    const initializeWallet = async () => {
      try {
        await setDoc(walletRef, { balance: 1000 }, { merge: true });
      } catch (error) {
        console.error('Error initializing wallet:', error);
      }
    };

    initializeWallet();

    // Real-time listener for balance changes
    const unsubscribe = onSnapshot(walletRef, (doc) => {
      if (doc.exists()) {
        setBalance(doc.data().balance || 0);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [currentUser]);

  const updateBalance = async (amount: number) => {
    if (!currentUser) return;

    try {
      const walletRef = doc(db, 'wallets', currentUser.uid);
      await updateDoc(walletRef, {
        balance: increment(amount)
      });
    } catch (error) {
      console.error('Error updating balance:', error);
      throw error;
    }
  };

  const value = {
    balance,
    updateBalance,
    loading
  };

  return (
    <WalletContext.Provider value={value}>
      {children}
    </WalletContext.Provider>
  );
};